<template>
  <div
    v-if="imageUrl"
    class="image-card"
    :class="sizeClass"
    :style="customSizeStyle"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="emit('click')">
    <div v-if="sortable" class="drag-handle" @click.stop>
      <IconGripVertical :size="20" color="#8E939D" />
    </div>
    <img :src="imageUrl" alt="Image" class="creative-image" draggable="false" />

    <video v-if="isHovered && videoUrl && !overlayMessage" class="video-overlay" autoplay loop muted playsinline>
      <source :src="videoUrl" type="video/mp4" />
    </video>

    <div v-if="selected" class="image-overlay selected-overlay">
      <IconCircleCheckFilled :size="48" />
    </div>

    <div v-else-if="modalSize && !overlayMessage" class="image-overlay add-overlay">
      <IconPhotoPlus :size="30" class="mb-10" stroke-width="1.5" />
      {{ $t('btn.select') || 'Select' }}
    </div>

    <div v-else-if="!hideDelete && !overlayMessage" class="image-overlay">
      <button
        class="delete-btn"
        @click.stop="emit('remove')"
        :title="$t('campaigns.adform.remove_creative') || 'Remove'">
        <IconTrashFilled :size="20" />
      </button>
    </div>

    <div v-if="overlayMessage" class="status-overlay">
      <div v-if="overlayError">
        <IconAlertHexagon :size="28" class="d-block mb-2 mx-auto" />
        <span>{{ overlayMessage }}</span>
      </div>
      <div v-else>
        <div class="spinner-border mb-2" role="status" style="width: 24px; height: 24px; border-width: 2px"></div>
        <br />
        <span>{{ overlayMessage }}</span>
      </div>
    </div>
  </div>

  <div v-else-if="overlayMessage" class="status-card dashed-border" :class="sizeClass" :style="customSizeStyle">
    <div class="status-card-content">
      <IconAlertHexagon v-if="overlayError" :size="28" class="mb-2" />
      <div v-else class="spinner-border mb-2" role="status" style="width: 24px; height: 24px; border-width: 2px"></div>
      <span>{{ overlayMessage }}</span>
    </div>
  </div>

  <div
    v-else
    class="upload-card dashed-border"
    :class="[sizeClass, { 'is-uploading': uploading, 'is-drag-over': isDragOver }]"
    :style="customSizeStyle"
    :aria-busy="uploading"
    :aria-disabled="uploading"
    @click="onUploadClick"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop">
    <div class="upload-content">
      <div class="upload-icon">
        <div
          v-if="uploading"
          class="spinner-border"
          role="status"
          :style="{ width: '28px', height: '28px', borderWidth: '2px' }"></div>
        <IconPhotoPlus v-else :size="30" stroke-width="1" />
      </div>
      <span>{{
        uploading ? $t('order_creation.media_step.uploading') : uploadText || $t('campaigns.click_to_upload')
      }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  IconTrashFilled,
  IconPhotoPlus,
  IconCircleCheckFilled,
  IconGripVertical,
  IconAlertHexagon,
} from '@tabler/icons-vue';

const isHovered = ref(false);

const props = withDefaults(
  defineProps<{
    imageUrl?: string | null;
    boxSize?: boolean;
    modalSize?: boolean;
    selected?: boolean;
    hideDelete?: boolean;
    uploadText?: string;
    sortable?: boolean;
    uploading?: boolean;
    width?: string | number;
    height?: string | number;
    videoUrl?: string | null;
    overlayMessage?: string | null;
    overlayError?: boolean;
  }>(),
  {
    boxSize: false,
    modalSize: false,
    selected: false,
    hideDelete: false,
    sortable: false,
    uploading: false,
    width: undefined,
    height: undefined,
    videoUrl: undefined,
    overlayMessage: undefined,
    overlayError: false,
  }
);

const emit = defineEmits<{
  click: [];
  remove: [];
  fileDrop: [file: File];
}>();

const sizeClass = computed(() => {
  return {
    'box-size': props.boxSize,
    'modal-size': props.modalSize,
  };
});

const customSizeStyle = computed(() => {
  if (!props.width && !props.height) return undefined;
  const style: Record<string, string> = {};
  if (props.width) style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;
  if (props.height) style.height = typeof props.height === 'number' ? `${props.height}px` : props.height;
  return style;
});

function onUploadClick() {
  if (props.uploading) return;
  emit('click');
}

const isDragOver = ref(false);

function onDragOver(e: DragEvent) {
  e.preventDefault();
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

function onDrop(e: DragEvent) {
  e.preventDefault();
  isDragOver.value = false;
  if (props.uploading) return;
  const file = e.dataTransfer?.files[0];
  if (file) emit('fileDrop', file);
}
</script>

<style scoped lang="scss">
.image-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #eaecf0;
  transition: border-color 0.2s;
  background-image:
    linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
  background-color: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    z-index: 1;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.2s;
    display: flex;
    justify-content: flex-end;
    color: #000;
    font-size: 13px;
    font-weight: 600;

    &.add-overlay {
      flex-direction: column;
      background: #ffffffcc;
      justify-content: center;
      align-items: center;
    }

    &.selected-overlay {
      background-color: rgba(255, 255, 255, 0.7);
      color: var(--tw-color-primary);
      justify-content: center;
      align-items: center;
      opacity: 1;
    }

    .delete-btn {
      background: rgba(255, 255, 255, 0.8);
      box-shadow: 0px 0px 4px 0px #00000040;
      border: none;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin-top: 4px;
      margin-right: 4px;
      color: #000;

      &:hover {
        color: #dc3545;
      }
    }
  }

  &:hover .image-overlay:not(.selected-overlay) {
    opacity: 1;
  }

  .status-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.55);
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-size: 12px;
    z-index: 2;
    padding: 8px;
  }
  .drag-handle {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 10;
    cursor: grab;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 4px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:active {
      cursor: grabbing;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 1);
    }
  }
}

.dashed-border {
  padding: 10px;
  border-radius: 10px;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23EAECF0' stroke-width='7' stroke-dasharray='6%2c 6'/%3e%3c/svg%3e");
}

.status-card {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 10px;

  .status-card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #8e939d;
    font-size: 12px;
    font-weight: 400;
    padding: 12px;
  }
}

.upload-card {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    opacity 0.2s;

  &:hover {
    border-color: #cfd2d6;
  }

  &.is-uploading {
    cursor: progress;
    pointer-events: none;
    opacity: 0.85;
  }

  &.is-drag-over {
    background-color: #f0f5ff;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%234A90D9' stroke-width='7' stroke-dasharray='6%2c 6'/%3e%3c/svg%3e");
  }

  .upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #8e939d;
    font-size: 12px;
    font-weight: 400;
  }

  .upload-icon {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8e939d;
  }
}

.box-size {
  width: 193px;
  height: 120px;
  aspect-ratio: 193 / 120;
}

.image-card.box-size {
  padding: 10px 25px;
  box-sizing: border-box;
}

.modal-size {
  width: 100%;
  aspect-ratio: 1;
}

.creative-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.mb-10 {
  margin-bottom: 10px;
}
</style>
